import { Box, Text } from "@chakra-ui/react";
import { ChevronsUpDown } from "lucide-react";

import { useSideMenu } from "@/contexts/SideMenuContext";
import { useMerchant } from "@/hooks/useMerchant";
import { usePortalContext } from "@/hooks/usePortalContext";
import { usePortalContexts } from "@/hooks/usePortalContexts";
import ContextAvatar from "./ContextAvatar";

type Props = { onOpen: () => void };

/**
 * Where you are, and — when there is more than one context to be in — the way
 * to somewhere else. Inert rather than hidden for a user with a single
 * comercio: it is a "where am I" indicator first and a switcher second.
 */
const ContextSelectorCard = ({ onOpen }: Props): JSX.Element | null => {
  const { isSideMenuOpen } = useSideMenu();
  const { context } = usePortalContext();
  const { merchant } = useMerchant();
  const { orgs, canSwitch } = usePortalContexts();

  const org = orgs.find((o) => o.id === context?.id);
  const primary =
    context?.type === "org"
      ? org?.displayName ?? ""
      : merchant?.displayName ?? "";
  // Blank for a merchant in an org the user does not hold: the feed only fills
  // `orgDisplayName` for orgs actually granted.
  const secondary =
    context?.type === "org" ? "Organización" : merchant?.orgDisplayName;

  if (!primary) return null;

  return (
    <Box
      mb={2}
      p={2}
      display="flex"
      alignItems="center"
      gap={2}
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="lg"
      justifyContent={isSideMenuOpen ? "flex-start" : "center"}
      cursor={canSwitch ? "pointer" : "default"}
      _hover={canSwitch ? { bg: "gray.50" } : undefined}
      onClick={canSwitch ? onOpen : undefined}
      title={isSideMenuOpen ? undefined : primary}
    >
      <ContextAvatar
        displayName={primary}
        kind={context?.type === "org" ? "org" : "merchant"}
      />
      {isSideMenuOpen && (
        <>
          <Box minW={0} flex="1">
            {/* Comercio names run long ("Conjunto Residencial Altos de la
                Colina"), and the sidebar is 250px. Wrap to two lines rather
                than cut the name off at the first word. */}
            <Text
              fontSize="sm"
              fontWeight={600}
              lineHeight="short"
              lineClamp={2}
              wordBreak="break-word"
            >
              {primary}
            </Text>
            {secondary && (
              <Text fontSize="xs" color="gray.500" truncate>
                {secondary}
              </Text>
            )}
          </Box>
          {canSwitch && (
            <ChevronsUpDown
              size={14}
              color="#9ca3af"
              style={{ flexShrink: 0 }}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default ContextSelectorCard;
